import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails, 
  CognitoUserAttribute 
} from 'amazon-cognito-identity-js';
import { API_URL } from '../../services/api/config';
import axios from 'axios';

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Enhanced environment variable validation and debugging
const validateCognitoConfig = () => {
  const config = {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    region: process.env.REACT_APP_COGNITO_REGION,
    apiUrl: process.env.REACT_APP_API_URL,
    environment: process.env.NODE_ENV,
    useLocalAuth: process.env.REACT_APP_USE_LOCAL_AUTH
  };

  const missing = [];
  const present = [];

  // Check required variables
  if (!config.userPoolId || config.userPoolId === 'local-user-pool') {
    missing.push('REACT_APP_COGNITO_USER_POOL_ID');
  } else {
    present.push('REACT_APP_COGNITO_USER_POOL_ID');
  }

  if (!config.clientId || config.clientId === 'local-client-id') {
    missing.push('REACT_APP_COGNITO_CLIENT_ID');
  } else {
    present.push('REACT_APP_COGNITO_CLIENT_ID');
  }

  if (!config.region) {
    missing.push('REACT_APP_COGNITO_REGION');
  } else {
    present.push('REACT_APP_COGNITO_REGION');
  }

  if (!config.apiUrl) {
    missing.push('REACT_APP_API_URL');
  } else {
    present.push('REACT_APP_API_URL');
  }

  // Enhanced logging for debugging
  console.group('🔐 Cognito Configuration Validation');
  console.log('Environment:', config.environment);
  console.log('Build timestamp:', new Date().toISOString());
  
  if (present.length > 0) {
    console.log('✅ Present variables:', present);
    present.forEach(varName => {
      const value = config[varName.toLowerCase().replace('react_app_cognito_', '').replace('react_app_', '')];
      if (value) {
        console.log(`   ${varName}: ${value.substring(0, 20)}${value.length > 20 ? '...' : ''}`);
      }
    });
  }

  if (missing.length > 0) {
    console.error('❌ Missing variables:', missing);
    console.error('This will cause "Authentication provider not available" error');
    
    if (isProduction) {
      console.error('🚨 PRODUCTION DEPLOYMENT ISSUE DETECTED:');
      console.error('   - Environment variables not included in build');
      console.error('   - Check deployment process and CloudFront cache');
      console.error('   - Verify .env.production was used during build');
    }
  }

  // Validate format of present variables
  if (config.userPoolId && config.userPoolId !== 'local-user-pool') {
    const userPoolIdPattern = /^[a-z0-9-]+_[a-zA-Z0-9]+$/;
    if (!userPoolIdPattern.test(config.userPoolId)) {
      console.warn('⚠️ User Pool ID format may be invalid:', config.userPoolId);
    }
  }

  if (config.clientId && config.clientId !== 'local-client-id') {
    const clientIdPattern = /^[a-z0-9]+$/;
    if (!clientIdPattern.test(config.clientId)) {
      console.warn('⚠️ Client ID format may be invalid:', config.clientId);
    }
  }

  console.groupEnd();

  return {
    isValid: missing.length === 0,
    missing,
    present,
    config,
    canUseCognito: missing.length === 0 && config.useLocalAuth !== 'true'
  };
};

// Validate configuration at startup
const cognitoValidation = validateCognitoConfig();

// Cognito configuration with fallbacks
const poolData = {
  UserPoolId: cognitoValidation.config.userPoolId || 'local-user-pool',
  ClientId: cognitoValidation.config.clientId || 'local-client-id'
};

// Create user pool if Cognito credentials are provided
// Only skip Cognito if explicitly using local auth in development
const useLocalAuth = isDevelopment && process.env.REACT_APP_USE_LOCAL_AUTH === 'true';

// Enhanced user pool creation with better error handling
let userPool = null;
let userPoolCreationError = null;

try {
  if (cognitoValidation.canUseCognito) {
    userPool = new CognitoUserPool(poolData);
    console.log('✅ Cognito User Pool created successfully');
  } else {
    console.log('⚠️ Cognito User Pool not created:',
      useLocalAuth ? 'Using local auth' : 'Missing configuration');
  }
} catch (error) {
  userPoolCreationError = error;
  console.error('❌ Failed to create Cognito User Pool:', error);
}

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  
  // Use refs to prevent race conditions and track navigation state
  const mountedRef = useRef(true);
  const authCheckInProgressRef = useRef(false);
  const navigationInProgressRef = useRef(false);
  const authStateTransitionRef = useRef(false);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Safe state setter that checks if component is still mounted
  const safeSetState = useCallback((setter) => {
    if (mountedRef.current) {
      setter();
    }
  }, []);

  // Navigation guard to prevent multiple simultaneous navigation attempts
  const preventNavigationRaceCondition = useCallback(() => {
    if (navigationInProgressRef.current || authStateTransitionRef.current) {
      return true; // Block navigation if already in progress
    }
    return false;
  }, []);

  // Safe authentication state setter with navigation guards
  const safeSetAuthState = useCallback((isAuth, userData = null) => {
    if (!mountedRef.current || authStateTransitionRef.current) {
      return;
    }
    
    authStateTransitionRef.current = true;
    
    safeSetState(() => {
      setCurrentUser(userData);
      setIsAuthenticated(isAuth);
      setLoading(false);
    });
    
    // Reset transition flag after state update
    setTimeout(() => {
      authStateTransitionRef.current = false;
    }, 10);
  }, [safeSetState]);

  const logout = useCallback(() => {
    // If using local auth in development mode
    if (useLocalAuth) {
      // Clear local storage
      localStorage.removeItem('localAuthToken');
      localStorage.removeItem('localUserData');
      localStorage.removeItem('localTokenData');
      localStorage.removeItem('token');
    } else if (userPool) {
      // Regular Cognito logout
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      localStorage.removeItem('token');
    }
    
    // Remove axios auth header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset state safely
    safeSetState(() => {
      setCurrentUser(null);
      setIsAuthenticated(false);
      setSession(null);
      setError(null);
    });
  }, [safeSetState]);

  useEffect(() => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgressRef.current) {
      return;
    }
    
    authCheckInProgressRef.current = true;
    
    // Add timeout to prevent infinite loading
    const authTimeout = setTimeout(() => {
      console.warn('Authentication check timed out, setting loading to false');
      if (mountedRef.current) {
        safeSetState(() => setLoading(false));
        authCheckInProgressRef.current = false;
      }
    }, 10000); // 10 second timeout
    
    const checkAuthStatus = async () => {
      try {
        // If using local auth in development mode (only when explicitly enabled)
        if (useLocalAuth) {
          // Check if there's a local auth token in localStorage
          const localAuthToken = localStorage.getItem('localAuthToken');
          
          if (localAuthToken) {
            try {
              const userData = JSON.parse(localStorage.getItem('localUserData') || '{}');
              
              // Validate token hasn't expired (if it has expiry)
              const tokenData = JSON.parse(localStorage.getItem('localTokenData') || '{}');
              if (tokenData.expires && Date.now() > tokenData.expires) {
                console.log('Local auth token expired, clearing local storage...');
                // Clear local storage directly instead of calling logout()
                localStorage.removeItem('localAuthToken');
                localStorage.removeItem('localUserData');
                localStorage.removeItem('localTokenData');
                localStorage.removeItem('token');
                delete axios.defaults.headers.common['Authorization'];
                safeSetState(() => {
                  setCurrentUser(null);
                  setIsAuthenticated(false);
                  setSession(null);
                  setError(null);
                  setLoading(false);
                });
                return;
              }
              
              safeSetState(() => {
                setCurrentUser(userData);
                setIsAuthenticated(true);
                
                // Also store token in localStorage with key 'token' for apiService.js
                localStorage.setItem('token', localAuthToken);
                
                // Set up axios auth header with the local token
                axios.defaults.headers.common['Authorization'] = `Bearer ${localAuthToken}`;
              });
            } catch (error) {
              console.error('Error parsing local user data:', error);
              // Clear local storage directly instead of calling logout()
              localStorage.removeItem('localAuthToken');
              localStorage.removeItem('localUserData');
              localStorage.removeItem('localTokenData');
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              safeSetState(() => {
                setCurrentUser(null);
                setIsAuthenticated(false);
                setSession(null);
                setError(null);
              });
            }
          }
          
          safeSetState(() => setLoading(false));
          return;
        }
        
        // Regular Cognito authentication flow
        if (userPool) {
          console.log('Checking for existing Cognito user...');
          const cognitoUser = userPool.getCurrentUser();
          
          if (cognitoUser) {
            console.log('Found existing Cognito user, getting session...');
            cognitoUser.getSession((err, session) => {
              if (!mountedRef.current) return;
              
              if (err) {
                console.error('Error getting session:', err);
                safeSetState(() => setLoading(false));
                return;
              }
              
              if (session.isValid()) {
                safeSetState(() => setSession(session));
                
                // Set up axios auth header with the ID token
                const idToken = session.getIdToken().getJwtToken();
                
                // Store token in localStorage with key 'token' for apiService.js
                localStorage.setItem('token', idToken);
                
                axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
                
                // Get user attributes
                cognitoUser.getUserAttributes((err, attributes) => {
                  if (!mountedRef.current) return;
                  
                  if (err) {
                    console.error('Error getting user attributes:', err);
                    // If we can't get attributes but have a valid session, still authenticate
                    // Use basic user data from the session
                    const basicUserData = {
                      username: cognitoUser.getUsername(),
                      email: cognitoUser.getUsername() // Username is typically email in Cognito
                    };
                    
                    safeSetState(() => {
                      setCurrentUser(basicUserData);
                      setIsAuthenticated(true);
                      setLoading(false);
                    });
                    return;
                  }
                  
                  const userData = {};
                  attributes.forEach(attr => {
                    userData[attr.getName()] = attr.getValue();
                  });
                  
                  safeSetState(() => {
                    setCurrentUser({
                      ...userData,
                      username: cognitoUser.getUsername()
                    });
                    setIsAuthenticated(true);
                    setLoading(false);
                  });
                });
              } else {
                // Session is not valid, clear session
                const cognitoUser = userPool.getCurrentUser();
                if (cognitoUser) {
                  cognitoUser.signOut();
                }
                localStorage.removeItem('token');
                delete axios.defaults.headers.common['Authorization'];
                safeSetState(() => {
                  setCurrentUser(null);
                  setIsAuthenticated(false);
                  setSession(null);
                  setError(null);
                  setLoading(false);
                });
              }
            });
          } else {
            safeSetState(() => setLoading(false));
          }
        } else {
          // Enhanced debugging when userPool is null
          console.group('⚠️ Cognito User Pool Not Available');
          console.log('Reason: userPool is null');
          console.log('Configuration validation:', cognitoValidation);
          console.log('useLocalAuth:', useLocalAuth);
          console.log('Environment:', process.env.NODE_ENV);
          
          if (userPoolCreationError) {
            console.error('User Pool creation error:', userPoolCreationError);
          }
          
          if (!cognitoValidation.isValid) {
            console.error('Missing environment variables:', cognitoValidation.missing);
            console.log('Available environment variables:', cognitoValidation.present);
          }
          
          if (isProduction) {
            console.error('🚨 Production Issue: Authentication will not work');
            console.error('User will see "Authentication provider not available" error on login');
          }
          
          console.groupEnd();
          
          safeSetState(() => setLoading(false));
        }
      } catch (error) {
        console.error('Error during auth check:', error);
        safeSetState(() => {
          setError('Authentication check failed');
          setLoading(false);
        });
      } finally {
        clearTimeout(authTimeout);
        authCheckInProgressRef.current = false;
      }
    };
    
    checkAuthStatus();
  }, [safeSetState]);

  const login = useCallback((email, password) => {
    return new Promise((resolve, reject) => {
      // For local auth, skip mount check at the beginning
      if (!useLocalAuth && !mountedRef.current) {
        reject(new Error('Component unmounted'));
        return;
      }
      
      safeSetState(() => {
        setError(null);
        setLoading(true);
      });
      
      // Check for navigation race conditions
      if (preventNavigationRaceCondition()) {
        reject(new Error('Authentication already in progress'));
        return;
      }
        
        // If using local auth in development mode (only when explicitly enabled)
        if (useLocalAuth) {
          // Validate required fields
          if (!email || !password) {
            safeSetState(() => {
              setError('Email and password are required');
              setLoading(false);
            });
            reject(new Error('Email and password are required'));
            return;
          }
          
          // In local development mode, validate against predefined credentials
          const validCredentials = [
            { email: 'mateusz@oh.xyz', password: 'TestPassword123!' },
            { email: 'admin@example.com', password: 'admin123!' }
          ];
          
          const validUser = validCredentials.find(
            cred => cred.email === email && cred.password === password
          );
          
          if (!validUser) {
            safeSetState(() => {
              setError('Invalid email or password');
              setLoading(false);
            });
            reject(new Error('Invalid email or password'));
            return;
          }
          
          // Create a mock token with expiration
          const mockToken = `local-auth-token-${Date.now()}`;
          const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
          
          // Create mock user data
          const userData = {
            email: email,
            name: email.split('@')[0],
            sub: `local-${Date.now()}`,
            username: email
          };
          
          // Store in localStorage for persistence
          localStorage.setItem('localAuthToken', mockToken);
          localStorage.setItem('localUserData', JSON.stringify(userData));
          localStorage.setItem('localTokenData', JSON.stringify({ expires: expiresAt }));
          
          // Also store token in localStorage with key 'token' for apiService.js
          localStorage.setItem('token', mockToken);
          
          // Set up axios auth header
          axios.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`;
          
          // Update state immediately without artificial delays
          safeSetAuthState(true, userData);
          resolve(userData);
          return;
        }
        
        // Regular Cognito authentication flow
        if (userPool) {
          const authenticationDetails = new AuthenticationDetails({
            Username: email,
            Password: password
          });
          
          const cognitoUser = new CognitoUser({
            Username: email,
            Pool: userPool
          });
          
          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (session) => {
              if (!mountedRef.current) {
                reject(new Error('Component unmounted'));
                return;
              }
              
              safeSetState(() => setSession(session));
              
              // Set up axios auth header with the ID token
              const idToken = session.getIdToken().getJwtToken();
              
              // Store token in localStorage with key 'token' for apiService.js
              localStorage.setItem('token', idToken);
              
              axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
              
              // Get user attributes
              cognitoUser.getUserAttributes((err, attributes) => {
                if (!mountedRef.current) {
                  reject(new Error('Component unmounted'));
                  return;
                }
                
                if (err) {
                  console.error('Error getting user attributes:', err);
                  // If we can't get attributes but have a valid session, still authenticate
                  // Use basic user data from the session
                  const basicUserData = {
                    username: cognitoUser.getUsername(),
                    email: cognitoUser.getUsername() // Username is typically email in Cognito
                  };
                  
                  safeSetAuthState(true, basicUserData);
                  resolve(basicUserData);
                  return;
                }
                
                const userData = {};
                attributes.forEach(attr => {
                  userData[attr.getName()] = attr.getValue();
                });
                
                const user = {
                  ...userData,
                  username: cognitoUser.getUsername()
                };
                
                // Update state immediately without artificial delays
                safeSetAuthState(true, user);
                resolve(user);
              });
            },
            onFailure: (err) => {
              console.error('Error during authentication:', err);
              safeSetState(() => {
                setError(err.message || 'Login failed. Please check your credentials.');
                setLoading(false);
              });
              reject(err);
            },
            newPasswordRequired: (userAttributes, requiredAttributes) => {
              // Handle new password required scenario
              safeSetState(() => {
                setError('You need to change your password before logging in.');
                setLoading(false);
              });
              reject(new Error('New password required'));
            }
          });
        } else {
          // Enhanced error handling with specific debugging information
          const errorDetails = [];
          
          if (userPoolCreationError) {
            errorDetails.push(`User Pool creation failed: ${userPoolCreationError.message}`);
          }
          
          if (!cognitoValidation.isValid) {
            errorDetails.push(`Missing environment variables: ${cognitoValidation.missing.join(', ')}`);
          }
          
          if (useLocalAuth) {
            errorDetails.push('Local auth is enabled in development mode');
          }
          
          const detailedError = errorDetails.length > 0
            ? `Authentication provider not available. Details: ${errorDetails.join('; ')}`
            : 'Authentication provider not available - Cognito configuration missing';
          
          console.error('🚨 Authentication Provider Error:', {
            userPoolExists: !!userPool,
            userPoolCreationError,
            cognitoValidation,
            useLocalAuth,
            environment: process.env.NODE_ENV,
            timestamp: new Date().toISOString()
          });
          
          if (isProduction) {
            console.error('🔧 Production Troubleshooting Steps:');
            console.error('1. Check CloudFront cache - may be serving old version');
            console.error('2. Verify deployment included .env.production file');
            console.error('3. Confirm build process embedded environment variables');
            console.error('4. Check browser network tab for correct JavaScript files');
          }
          
          safeSetState(() => {
            setError(detailedError);
            setLoading(false);
          });
          reject(new Error(detailedError));
        }
    });
  }, [safeSetState, preventNavigationRaceCondition, safeSetAuthState]);


  const signUp = (email, password, attributes = {}) => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);
      
      const attributeList = [];
      
      // Add user attributes
      Object.keys(attributes).forEach(key => {
        attributeList.push(
          new CognitoUserAttribute({
            Name: key,
            Value: attributes[key]
          })
        );
      });
      
      userPool.signUp(email, password, attributeList, null, (err, result) => {
        setLoading(false);
        
        if (err) {
          console.error('Error during sign up:', err);
          setError(err.message || 'Sign up failed. Please try again.');
          reject(err);
          return;
        }
        
        resolve(result.user);
      });
    });
  };

  const confirmSignUp = (email, code) => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);
      
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });
      
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        setLoading(false);
        
        if (err) {
          console.error('Error during confirmation:', err);
          setError(err.message || 'Confirmation failed. Please try again.');
          reject(err);
          return;
        }
        
        resolve(result);
      });
    });
  };

  const forgotPassword = (email) => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);
      
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });
      
      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          setLoading(false);
          resolve(data);
        },
        onFailure: (err) => {
          console.error('Error during forgot password:', err);
          setError(err.message || 'Password reset failed. Please try again.');
          setLoading(false);
          reject(err);
        }
      });
    });
  };

  const confirmPassword = (email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      setError(null);
      setLoading(true);
      
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });
      
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          setLoading(false);
          resolve();
        },
        onFailure: (err) => {
          console.error('Error during password confirmation:', err);
          setError(err.message || 'Password reset confirmation failed. Please try again.');
          setLoading(false);
          reject(err);
        }
      });
    });
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    signUp,
    confirmSignUp,
    forgotPassword,
    confirmPassword,
    session,
    preventNavigationRaceCondition,
    // Enhanced debugging information
    debugInfo: {
      cognitoValidation,
      userPoolExists: !!userPool,
      userPoolCreationError,
      useLocalAuth,
      environment: process.env.NODE_ENV,
      configurationStatus: cognitoValidation.isValid ? 'valid' : 'invalid'
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
